"use client";

/**
 * SmartLink
 * Public-view wrapper that renders `children` inside an interactive element that adapts to the link type:
 * - Form link (/{eventId}/form/{formId}) → button that opens FormModal in-place
 * - Anchor link (#section) → smooth-scrolls
 * - External link → opens in a new tab
 * - Otherwise → regular <a> navigation
 *
 * Use SmartLink when you need richer content than a plain text label (e.g., a card tile).
 * Use SmartButton when the content is a simple text label.
 */

import type React from "react";
import { useCallback, useState } from "react";
import { isAnchorLink, isExternalLink } from "../../_logic/smart-link-utils";
import { getStickyHeaderOffset } from "../../_logic/utils/scroll-utils";
import { prefetchHotelBookingModal } from "../../_logic/hotel-booking-modal-prefetch";
import { useHotelBookingCta } from "../../_hooks/useHotelBookingCta";
import { useSmartLinkTarget } from "../../_hooks/useSmartLinkTarget";
import { FormModal } from "./FormModal";

interface SmartLinkProps {
  /** The link URL — form, external, anchor, or relative */
  href: string;
  /** Event ID for form link detection */
  eventId: number;
  /** Primary color for FormModal styling */
  primaryColor: string;
  /** Wrapper element classes (applied to the rendered <a> or <button>) */
  className?: string;
  /** Wrapper element children — the visible content of the link */
  children: React.ReactNode;
  /** Accessible label override (used as aria-label when children aren't pure text) */
  ariaLabel?: string;
}

/** Wraps arbitrary content with smart link routing (form modal / anchor scroll / external tab / normal nav) */
export function SmartLink({
  href,
  eventId,
  primaryColor,
  className,
  children,
  ariaLabel,
}: SmartLinkProps): React.ReactElement {
  // States
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  // Derived values
  const target = useSmartLinkTarget(href, eventId);
  const isFormLink = target.kind === "form";
  const formId = target.kind === "form" ? target.formId : null;
  const inviteToken = target.kind === "form" ? target.inviteToken : null;
  const isHotelBookingLink = target.kind === "hotel-booking";
  const bookingLinkId = target.kind === "hotel-booking" ? target.bookingLinkId : null;
  const { open: openHotelBookingModal, modal: hotelBookingModal } = useHotelBookingCta(eventId, bookingLinkId);
  const interactiveClassName = `touch-manipulation ${className ?? ""}`.trim();

  // Handlers
  const handleAnchorClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset(24);
        window.scrollTo({ top, behavior: "smooth" });
      }
    },
    [href]
  );

  // Reject dangerous schemes (javascript:/data:/…) before rendering an href.
  // Stored links are organizer free-text, so guard at the renderer regardless of the editor.
  // Email links (`#__email-{id}`) can't send from the surfaces SmartLink wraps, so they render inert too.
  if (target.kind === "unsafe" || target.kind === "email") {
    return <span className={className}>{children}</span>;
  }

  // Render — form link → button + modal
  if (isFormLink && formId) {
    return (
      <>
        <button
          type="button"
          onClick={() => setIsFormModalOpen(true)}
          className={interactiveClassName}
          aria-label={ariaLabel}
        >
          {children}
        </button>
        <FormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          formId={formId}
          eventId={eventId}
          primaryColor={primaryColor}
          inviteToken={inviteToken}
        />
      </>
    );
  }

  // Render — hotel-booking link → button + modal
  if (isHotelBookingLink && bookingLinkId) {
    return (
      <>
        <button
          type="button"
          onClick={openHotelBookingModal}
          // Warm the modal chunk on real intent (hover/touch) rather than unconditionally at idle
          // for every page that happens to have this CTA — most visitors never click it.
          onPointerEnter={prefetchHotelBookingModal}
          onTouchStart={prefetchHotelBookingModal}
          className={interactiveClassName}
          aria-label={ariaLabel}
        >
          {children}
        </button>
        {hotelBookingModal}
      </>
    );
  }

  // Render — anchor link (#section)
  if (isAnchorLink(href)) {
    return (
      <a href={href} onClick={handleAnchorClick} className={interactiveClassName} aria-label={ariaLabel}>
        {children}
      </a>
    );
  }

  // Render — external or regular link
  const external = isExternalLink(href);
  return (
    <a
      href={href}
      className={interactiveClassName}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      aria-label={ariaLabel}
    >
      {children}
      {external && <span className="sr-only">(opens in new tab)</span>}
    </a>
  );
}
